import { Component } from '@theme/component';

/**
 * Collection menu component that handles overflow list with floating panel
 */
export class CollectionMenuComponent extends Component {
  /**
   * Required refs for this component
   * @type {string[]}
   */
  requiredRefs = ['panel'];

  /**
   * Tracks if the panel is currently open
   * @type {boolean}
   */
  #isOpen = false;

  connectedCallback() {
    super.connectedCallback();

    // Close panel when clicking outside
    document.addEventListener('click', this.#handleDocumentClick);

    // Close panel on escape key
    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * Shows the floating panel with overflow menu items
   * @param {Event} event
   */
  showMore = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if there are any overflow items before opening
    const overflowList = this.querySelector('overflow-list');
    if (overflowList) {
      const overflowItems = overflowList.querySelectorAll('li[slot="overflow"]');
      if (overflowItems.length === 0) {
        return; // Don't open panel if no overflow items
      }
    }

    if (this.#isOpen) {
      this.#closePanel();
    } else {
      this.#openPanel();
    }
  };

  /**
   * Opens the floating panel
   */
  #openPanel = () => {
    const panel = /** @type {HTMLElement} */ (this.refs.panel);

    if (!panel) return;

    // Hide items in panel that are already visible in the main list
    this.#updatePanelVisibility();

    panel.style.display = 'block';
    this.#isOpen = true;

    // Position the panel relative to the more button
    requestAnimationFrame(() => {
      this.#positionPanel();
    });
  };

  /**
   * Closes the floating panel
   */
  #closePanel = () => {
    const panel = /** @type {HTMLElement} */ (this.refs.panel);

    if (!panel) return;

    panel.style.display = 'none';
    this.#isOpen = false;
  };

  /**
   * Updates panel visibility by hiding items that are already visible in the main list
   */
  #updatePanelVisibility = () => {
    const overflowList = this.querySelector('overflow-list');
    const panel = /** @type {HTMLElement} */ (this.refs.panel);

    if (!overflowList || !panel) return;

    // Get all menu items in the overflow-list (they are direct children)
    const menuItems = /** @type {NodeListOf<HTMLLIElement>} */ (
      overflowList.querySelectorAll('li')
    );

    // Get the text content of items that are currently visible (not in overflow slot)
    const visibleTexts = Array.from(menuItems)
      .filter(item => item.slot !== 'overflow')
      .map(item => {
        const link = /** @type {HTMLAnchorElement} */ (item.querySelector('a[href]'));
        return link ? link.textContent?.trim() : null;
      })
      .filter(text => text !== null);

    // Hide corresponding links in the panel
    const panelLinks = /** @type {NodeListOf<HTMLLIElement>} */ (
      panel.querySelectorAll('li')
    );
    
    panelLinks.forEach(li => {
      const link = /** @type {HTMLAnchorElement} */ (li.querySelector('a[href]'));
      if (link) {
        const linkText = link.textContent?.trim();
        const shouldHide = visibleTexts.includes(linkText);
        if (shouldHide) {
          li.classList.add('hidden');
        } else {
          li.classList.remove('hidden');
        }
      }
    });
  };

  /**
   * Positions the panel relative to the more button
   */
  #positionPanel = () => {
    const panel = /** @type {HTMLElement} */ (this.refs.panel);
    const moreButton = this.querySelector('slot[name="more"] button');

    if (!panel || !moreButton) return;

    const buttonRect = moreButton.getBoundingClientRect();
    const containerRect = this.getBoundingClientRect();

    // Position panel below the more button
    const top = buttonRect.bottom - containerRect.top + 8;
    const left = buttonRect.left - containerRect.left;

    panel.style.position = 'absolute';
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
    panel.style.zIndex = 'var(--layer-raised)';
  };

  /**
   * Handles clicks outside the component to close the panel
   * @param {Event} event
   */
  #handleDocumentClick = (event) => {
    if (!this.#isOpen) return;

    const target = /** @type {Node} */ (event.target);
    if (target && !this.contains(target)) {
      this.#closePanel();
    }
  };

  /**
   * Handles escape key to close the panel
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) {
      this.#closePanel();
    }
  };
}

if (!customElements.get('collection-menu-component')) {
  customElements.define('collection-menu-component', CollectionMenuComponent);
}